/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';

import type { ScanNoteVisibility } from '@kaiizen/kai-pro-api';

import { styleguideColors as COLORS, styleguideFontWeight as FONT_WEIGHT } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { DEFAULT_SIDE_DRAWER_STYLES, type SideDrawerStyles } from '@/Client/Widgets/SideDrawer';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

export type ScopeNotesDrawerStyles = {
  /** Passed straight through to the drawer shell. */
  drawer: SideDrawerStyles;
  crumbs: SerializedStyles;
  crumb: SerializedStyles;
  crumbButton: SerializedStyles;
  currentCrumbButton: SerializedStyles;
  title: SerializedStyles;
  count: SerializedStyles;
  empty: SerializedStyles;
  note: SerializedStyles;
  /* Internal is the quiet default; external carries an ink edge and a ground of its own, so the two
     never rely on a single cue to tell them apart. */
  noteByVisibility: Record<ScanNoteVisibility, SerializedStyles>;
  noteMeta: SerializedStyles;
  noteAuthor: SerializedStyles;
  noteWhen: SerializedStyles;
  noteActions: SerializedStyles;
  textButton: SerializedStyles;
  dangerTextButton: SerializedStyles;
  noteBody: SerializedStyles;
  chips: SerializedStyles;
  chip: SerializedStyles;
  visibilityChipByVisibility: Record<ScanNoteVisibility, SerializedStyles>;
  confirmRow: SerializedStyles;
  confirmButton: SerializedStyles;
  dangerConfirmButton: SerializedStyles;
  textarea: SerializedStyles;
  composer: SerializedStyles;
  composerRow: SerializedStyles;
  visibilityToggle: SerializedStyles;
  visibilityOption: SerializedStyles;
  selectedVisibilityOptionByVisibility: Record<ScanNoteVisibility, SerializedStyles>;
  hint: SerializedStyles;
  saveButton: SerializedStyles;
};

const MONO_CAPTION = css`
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: ${FONT_WEIGHT.bold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const CHIP = css`
  ${MONO_CAPTION};
  font-size: ${pxToRem(9)};
  padding: 3px 7px;
  border: 1px solid ${COLORS.stroke};
  color: ${COLORS.midtone};
  background: ${COLORS.white};
`;

export const DEFAULT_SCOPE_NOTES_DRAWER_STYLES: ScopeNotesDrawerStyles = {
  drawer: {
    ...DEFAULT_SIDE_DRAWER_STYLES,
    body: css`
      ${DEFAULT_SIDE_DRAWER_STYLES.body};
      label: SCOPE_NOTES_DRAWER_LIST;
      display: flex;
      flex-direction: column;
      gap: 12px;
    `,
  },
  crumbs: css`
    label: SCOPE_NOTES_DRAWER_CRUMBS;
    ${MONO_CAPTION};
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    color: ${COLORS.midtone};
  `,
  crumb: css`
    label: SCOPE_NOTES_DRAWER_CRUMB;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  `,
  crumbButton: css`
    label: SCOPE_NOTES_DRAWER_CRUMB_BUTTON;
    ${MONO_CAPTION};
    border: 0;
    padding: 0;
    background: none;
    cursor: pointer;
    color: ${COLORS.midtone};
    text-underline-offset: 3px;
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
      color: ${COLORS.ink};
    }
  `,
  currentCrumbButton: css`
    label: SCOPE_NOTES_DRAWER_CRUMB_BUTTON_CURRENT;
    cursor: default;
    color: ${COLORS.ink};
    text-decoration: underline;
  `,
  title: css`
    label: SCOPE_NOTES_DRAWER_TITLE;
    margin: 0;
    font-family: ${FONT.display};
    font-size: ${pxToRem(22)};
    font-weight: ${FONT_WEIGHT.bold};
    color: ${COLORS.ink};
    line-height: 1.2;
    overflow-wrap: anywhere;
  `,
  count: css`
    label: SCOPE_NOTES_DRAWER_COUNT;
    ${MONO_CAPTION};
    color: ${COLORS.midtone};
  `,
  empty: css`
    label: SCOPE_NOTES_DRAWER_EMPTY;
    font-family: ${FONT.body};
    font-size: ${pxToRem(13)};
    color: ${COLORS.textTertiary};
  `,
  note: css`
    label: SCOPE_NOTES_DRAWER_NOTE;
    position: relative;
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  `,
  noteByVisibility: {
    internal: css`
      label: SCOPE_NOTES_DRAWER_NOTE_INTERNAL;
      border: 1px solid ${COLORS.stroke};
      background: ${COLORS.paper};
    `,
    external: css`
      label: SCOPE_NOTES_DRAWER_NOTE_EXTERNAL;
      border: 1px solid ${COLORS.ink};
      background: ${COLORS.sunken};
    `,
  },
  noteMeta: css`
    label: SCOPE_NOTES_DRAWER_NOTE_META;
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 8px;
  `,
  noteAuthor: css`
    label: SCOPE_NOTES_DRAWER_NOTE_AUTHOR;
    font-family: ${FONT.body};
    font-size: ${pxToRem(14)};
    font-weight: ${FONT_WEIGHT.bold};
    color: ${COLORS.ink};
  `,
  noteWhen: css`
    label: SCOPE_NOTES_DRAWER_NOTE_WHEN;
    font-family: ${FONT.mono};
    font-size: ${pxToRem(10)};
    color: ${COLORS.textTertiary};
  `,
  noteActions: css`
    label: SCOPE_NOTES_DRAWER_NOTE_ACTIONS;
    margin-left: auto;
    display: inline-flex;
    gap: 10px;
  `,
  textButton: css`
    label: SCOPE_NOTES_DRAWER_TEXT_BUTTON;
    ${MONO_CAPTION};
    border: 0;
    padding: 0;
    background: none;
    cursor: pointer;
    color: ${COLORS.midtone};

    &:hover {
      color: ${COLORS.ink};
      text-decoration: underline;
    }

    &:disabled {
      opacity: 0.5;
      cursor: default;
      text-decoration: none;
    }
  `,
  dangerTextButton: css`
    label: SCOPE_NOTES_DRAWER_TEXT_BUTTON_DANGER;
    color: ${COLORS.error};

    &:hover {
      color: ${COLORS.error};
    }
  `,
  noteBody: css`
    label: SCOPE_NOTES_DRAWER_NOTE_BODY;
    font-family: ${FONT.body};
    font-size: ${pxToRem(14)};
    line-height: 1.5;
    color: ${COLORS.ink};
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  `,
  chips: css`
    label: SCOPE_NOTES_DRAWER_CHIPS;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  `,
  chip: css`
    label: SCOPE_NOTES_DRAWER_CHIP;
    ${CHIP};
  `,
  visibilityChipByVisibility: {
    internal: css`
      label: SCOPE_NOTES_DRAWER_VISIBILITY_CHIP_INTERNAL;
      ${CHIP};
      border-color: transparent;
      background: ${COLORS.stroke};
      color: ${COLORS.ink};
    `,
    external: css`
      label: SCOPE_NOTES_DRAWER_VISIBILITY_CHIP_EXTERNAL;
      ${CHIP};
      border-color: transparent;
      background: ${COLORS.midtone};
      color: ${COLORS.white};
    `,
  },
  confirmRow: css`
    label: SCOPE_NOTES_DRAWER_CONFIRM_ROW;
    display: flex;
    gap: 8px;
  `,
  confirmButton: css`
    label: SCOPE_NOTES_DRAWER_CONFIRM_BUTTON;
    ${MONO_CAPTION};
    padding: 8px 12px;
    cursor: pointer;
    border: 1px solid ${COLORS.stroke};
    background: ${COLORS.white};
    color: ${COLORS.ink};
  `,
  dangerConfirmButton: css`
    label: SCOPE_NOTES_DRAWER_CONFIRM_BUTTON_DANGER;
    border-color: ${COLORS.error};
    background: ${COLORS.error};
    color: ${COLORS.white};
  `,
  textarea: css`
    label: SCOPE_NOTES_DRAWER_TEXTAREA;
    width: 100%;
    box-sizing: border-box;
    resize: vertical;
    font-family: ${FONT.body};
    font-size: ${pxToRem(14)};
    line-height: 1.5;
    color: ${COLORS.ink};
    background: ${COLORS.white};
    border: 1px solid ${COLORS.stroke};
    padding: 12px 14px;

    &:focus {
      outline: none;
      border-color: ${COLORS.ink};
    }

    &::placeholder {
      color: ${COLORS.textTertiary};
    }
  `,
  composer: css`
    label: SCOPE_NOTES_DRAWER_COMPOSER;
    display: flex;
    flex-direction: column;
    gap: 12px;
  `,
  composerRow: css`
    label: SCOPE_NOTES_DRAWER_COMPOSER_ROW;
    display: flex;
    align-items: center;
    gap: 12px;
  `,
  visibilityToggle: css`
    label: SCOPE_NOTES_DRAWER_VISIBILITY_TOGGLE;
    display: inline-flex;
    border: 1px solid ${COLORS.stroke};

    & > * + * {
      border-left: 1px solid ${COLORS.stroke};
    }
  `,
  visibilityOption: css`
    label: SCOPE_NOTES_DRAWER_VISIBILITY_OPTION;
    ${MONO_CAPTION};
    padding: 9px 14px;
    border: 0;
    cursor: pointer;
    background: ${COLORS.white};
    color: ${COLORS.ink};
  `,
  selectedVisibilityOptionByVisibility: {
    internal: css`
      label: SCOPE_NOTES_DRAWER_VISIBILITY_OPTION_INTERNAL;
      background: ${COLORS.stroke};
    `,
    external: css`
      label: SCOPE_NOTES_DRAWER_VISIBILITY_OPTION_EXTERNAL;
      background: ${COLORS.midtone};
      color: ${COLORS.white};
    `,
  },
  hint: css`
    label: SCOPE_NOTES_DRAWER_HINT;
    display: inline-flex;
    color: ${COLORS.midtone};
    cursor: help;
  `,
  saveButton: css`
    label: SCOPE_NOTES_DRAWER_SAVE;
    ${MONO_CAPTION};
    margin-left: auto;
    padding: 10px 18px;
    cursor: pointer;
    border: 1px solid ${COLORS.ink};
    background: ${COLORS.ink};
    color: ${COLORS.white};

    &:disabled {
      cursor: not-allowed;
      border-color: ${COLORS.stroke};
      background: ${COLORS.white};
      color: ${COLORS.textTertiary};
    }
  `,
};
